/**
 * ELK-based graph layout for the provenance DAG.
 *
 * Takes React Flow nodes/edges and returns them with computed positions.
 * Uses orthogonal edge routing so edges bend around nodes instead of
 * cutting through them.
 */

import ELK, { type ElkNode, type ElkExtendedEdge } from "elkjs/lib/elk.bundled.js";
import type { Node, Edge } from "@xyflow/react";
import {
  ALL_DATA_NODE_WIDTH,
  COMMENT_BADGE_HEIGHT,
  COMMENT_BADGE_WIDTH,
  DETAIL_PILL_HEIGHT,
  DETAIL_PILL_WIDTH,
  PLOT_NODE_HEIGHT,
  PLOT_NODE_WIDTH,
  getAllDataNodeHeight,
  getRefQuantityGroupSize,
  getSourceGroupSize,
  getSweepGroupSize,
} from "./dagNodeSizing";

const elk = new ELK();

export interface ElkPoint {
  x: number;
  y: number;
}

function sizeFromNode(node: Node): { width: number; height: number } {
  const style = (node.style ?? {}) as {
    width?: number | string;
    height?: number | string;
    minHeight?: number | string;
  };

  const parseSize = (value: number | string | undefined): number | undefined => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = Number.parseFloat(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return undefined;
  };

  return {
    width:
      node.measured?.width ??
      node.width ??
      parseSize(style.width) ??
      COMMENT_BADGE_WIDTH,
    height:
      node.measured?.height ??
      node.height ??
      parseSize(style.height) ??
      parseSize(style.minHeight) ??
      COMMENT_BADGE_HEIGHT,
  };
}

function validEdgesFor(nodes: Node[], edges: Edge[]): Edge[] {
  const nodeIds = new Set(nodes.map((n) => n.id));
  return edges.filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target));
}

export async function layoutDAG(
  nodes: Node[],
  edges: Edge[],
): Promise<{ nodes: Node[]; edges: Edge[] }> {
  const validEdges = validEdgesFor(nodes, edges);

  const graph: ElkNode = {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered",
      "elk.direction": "DOWN",
      "elk.spacing.nodeNode": "60",
      "elk.layered.spacing.nodeNodeBetweenLayers": "100",
      "elk.edgeRouting": "ORTHOGONAL",
      "elk.spacing.edgeNode": "25",
      "elk.spacing.edgeEdge": "15",
    },
    children: nodes.map((n) => {
      if (n.type === "detailPill") {
        return { id: n.id, width: DETAIL_PILL_WIDTH, height: DETAIL_PILL_HEIGHT };
      }
      if (n.type === "allDataNode") {
        const runCount = (n.data as { runCount?: number })?.runCount ?? 6;
        return {
          id: n.id,
          width: ALL_DATA_NODE_WIDTH,
          height: getAllDataNodeHeight(runCount),
          layoutOptions: { "elk.layered.layerConstraint": "LAST" },
        };
      }
      if (n.type === "sweepGroup") {
        const d = n.data as { expanded?: boolean; group?: { runs: unknown[] } };
        return {
          id: n.id,
          ...getSweepGroupSize({
            expanded: d.expanded,
            runCount: d.group?.runs.length ?? 0,
          }),
        };
      }
      if (n.type === "refQuantityGroup") {
        const d = n.data as {
          expanded?: boolean;
          labels?: unknown[];
          group?: { surfaceMeshNodes?: unknown[] };
        };
        return {
          id: n.id,
          ...getRefQuantityGroupSize({
            expanded: d.expanded,
            labelCount: d.labels?.length ?? 2,
            hasMeshButton: (d.group?.surfaceMeshNodes?.length ?? 0) > 0,
          }),
        };
      }
      if (n.type === "sourceGroup") {
        const d = n.data as { expanded?: boolean; group?: { nodes: unknown[] } };
        return {
          id: n.id,
          ...getSourceGroupSize({
            expanded: d.expanded,
            nodeCount: d.group?.nodes?.length ?? 0,
          }),
        };
      }
      if (n.type === "plotNode") {
        return { id: n.id, width: PLOT_NODE_WIDTH, height: PLOT_NODE_HEIGHT };
      }
      if (n.type === "commentBadge") {
        return { id: n.id, width: COMMENT_BADGE_WIDTH, height: COMMENT_BADGE_HEIGHT };
      }
      return { id: n.id, ...sizeFromNode(n) };
    }),
    edges: validEdges.map((e) => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
  };

  const ELK_TIMEOUT_MS = 5000;
  let result;
  try {
    result = await Promise.race([
      elk.layout(graph),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("ELK layout timed out")), ELK_TIMEOUT_MS),
      ),
    ]);
  } catch (e) {
    console.warn("ELK layout failed or timed out, using fallback grid", e);
    return {
      nodes: nodes.map((n, i) => ({
        ...n,
        position: { x: (i % 10) * 200, y: Math.floor(i / 10) * 150 },
      })),
      edges,
    };
  }

  const elkNodeMap = new Map((result.children ?? []).map((c) => [c.id, c]));
  const positionedNodes = nodes.map((node) => {
    const elkNode = elkNodeMap.get(node.id);
    return {
      ...node,
      position: {
        x: elkNode?.x ?? 0,
        y: elkNode?.y ?? 0,
      },
    };
  });

  const elkEdgeMap = new Map<string, ElkExtendedEdge>();
  for (const e of (result.edges ?? []) as ElkExtendedEdge[]) {
    elkEdgeMap.set(e.id, e);
  }

  const positionedEdges = validEdges.map((edge) => {
    const elkEdge = elkEdgeMap.get(edge.id);
    const sections = elkEdge?.sections ?? [];
    const points: ElkPoint[] = [];
    for (const section of sections) {
      points.push(section.startPoint);
      if (section.bendPoints) points.push(...section.bendPoints);
      points.push(section.endPoint);
    }
    if (points.length >= 2) {
      return {
        ...edge,
        type: "elkRouted",
        data: { ...(edge.data ?? {}), elkPoints: points },
      };
    }
    return edge;
  });

  return { nodes: positionedNodes, edges: positionedEdges };
}

interface ForcePoint {
  x: number;
  y: number;
}

function topoDepths(nodes: Node[], edges: Edge[]): Map<string, number> {
  const ids = new Set(nodes.map((node) => node.id));
  const indegree = new Map(nodes.map((node) => [node.id, 0]));
  const outgoing = new Map<string, string[]>();

  for (const edge of edges) {
    if (!ids.has(edge.source) || !ids.has(edge.target)) continue;
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
    const next = outgoing.get(edge.source) ?? [];
    next.push(edge.target);
    outgoing.set(edge.source, next);
  }

  const depths = new Map(nodes.map((node) => [node.id, 0]));
  const queue = nodes
    .filter((node) => (indegree.get(node.id) ?? 0) === 0)
    .map((node) => node.id)
    .sort();

  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const target of outgoing.get(current) ?? []) {
      depths.set(target, Math.max(depths.get(target) ?? 0, (depths.get(current) ?? 0) + 1));
      indegree.set(target, (indegree.get(target) ?? 1) - 1);
      if ((indegree.get(target) ?? 0) === 0) {
        queue.push(target);
        queue.sort();
      }
    }
  }

  return depths;
}

function initialForcePositions(nodes: Node[], edges: Edge[]): Map<string, ForcePoint> {
  const depths = topoDepths(nodes, edges);
  const layers = new Map<number, Node[]>();
  for (const node of nodes) {
    const depth = depths.get(node.id) ?? 0;
    const layer = layers.get(depth) ?? [];
    layer.push(node);
    layers.set(depth, layer);
  }

  const positions = new Map<string, ForcePoint>();
  for (const [depth, layer] of layers) {
    layer.sort((a, b) => a.id.localeCompare(b.id));
    const centeredOffset = (layer.length - 1) / 2;
    layer.forEach((node, index) => {
      positions.set(node.id, {
        x: (index - centeredOffset) * 320,
        y: depth * 220,
      });
    });
  }
  return positions;
}

function stripElkRouting(edge: Edge): Edge {
  const { elkPoints: _elkPoints, ...data } = (edge.data ?? {}) as Record<string, unknown>;
  return {
    ...edge,
    type: "smoothstep",
    data,
  };
}

export function layoutForceGraph(
  nodes: Node[],
  edges: Edge[],
): { nodes: Node[]; edges: Edge[] } {
  const validEdges = validEdgesFor(nodes, edges);
  if (nodes.length === 0) return { nodes, edges: [] };

  const positions = initialForcePositions(nodes, validEdges);
  const sizes = new Map(nodes.map((node) => [node.id, sizeFromNode(node)]));
  const ids = nodes.map((node) => node.id);

  for (let iteration = 0; iteration < 180; iteration += 1) {
    const displacement = new Map(ids.map((id) => [id, { x: 0, y: 0 }]));

    for (let i = 0; i < ids.length; i += 1) {
      for (let j = i + 1; j < ids.length; j += 1) {
        const aId = ids[i]!;
        const bId = ids[j]!;
        const a = positions.get(aId)!;
        const b = positions.get(bId)!;
        const aSize = sizes.get(aId)!;
        const bSize = sizes.get(bId)!;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
          dx = 1;
          dy = 1;
        }
        const distanceSq = Math.max(dx * dx + dy * dy, 1);
        const distance = Math.sqrt(distanceSq);
        const minDistance = Math.max(aSize.width, bSize.width) * 0.88 + 90;
        const force = Math.min(9000 / distanceSq + Math.max(minDistance - distance, 0) * 0.22, 42);
        const fx = (dx / distance) * force;
        const fy = (dy / distance) * force;
        displacement.get(aId)!.x -= fx;
        displacement.get(aId)!.y -= fy;
        displacement.get(bId)!.x += fx;
        displacement.get(bId)!.y += fy;
      }
    }

    for (const edge of validEdges) {
      const source = positions.get(edge.source)!;
      const target = positions.get(edge.target)!;
      const dx = target.x - source.x;
      const dy = target.y - source.y;
      const distance = Math.max(Math.sqrt(dx * dx + dy * dy), 1);
      const desiredDistance = 280;
      const spring = (distance - desiredDistance) * 0.018;
      const fx = (dx / distance) * spring;
      const fy = (dy / distance) * spring;
      displacement.get(edge.source)!.x += fx;
      displacement.get(edge.source)!.y += fy;
      displacement.get(edge.target)!.x -= fx;
      displacement.get(edge.target)!.y -= fy;

      const verticalBias = (dy - 210) * 0.012;
      displacement.get(edge.source)!.y += verticalBias;
      displacement.get(edge.target)!.y -= verticalBias;
    }

    const cooling = 0.14 * (1 - iteration / 220);
    for (const id of ids) {
      const position = positions.get(id)!;
      const delta = displacement.get(id)!;
      const maxStep = 28;
      position.x += Math.max(-maxStep, Math.min(maxStep, delta.x * cooling));
      position.y += Math.max(-maxStep, Math.min(maxStep, delta.y * cooling));
    }
  }

  let minX = Infinity;
  let minY = Infinity;
  for (const node of nodes) {
    const position = positions.get(node.id)!;
    minX = Math.min(minX, position.x);
    minY = Math.min(minY, position.y);
  }

  const positionedNodes = nodes.map((node) => {
    const position = positions.get(node.id)!;
    return {
      ...node,
      position: {
        x: position.x - minX + 60,
        y: position.y - minY + 60,
      },
    };
  });

  return {
    nodes: positionedNodes,
    edges: validEdges.map(stripElkRouting),
  };
}
